import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddTokenForm, { TokenFormData } from './AddTokenForm';
import { 
  getTokenListAPI, 
  saveTokenAPI, 
  deleteTokenAPI,
  Token,
  TokenSearchRequest 
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './TokenMaster.module.css';

const TokenMaster: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [editingToken, setEditingToken] = useState<Token | null>(null);
  const permissions = useModulePermission('Token Master');

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: TokenSearchRequest = {
        searchText: '',
        pageNo: 1,
        pageSize: 100,
      };
      const response = await getTokenListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setTokens(response.data);
      } else {
        showError(response.message || 'Failed to load tokens');
      }
    } catch (error) {
      console.error('Failed to load tokens:', error);
      showError('Failed to load tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToken = () => {
    setEditingToken(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingToken(null);
  };

  const handleSubmitToken = async (tokenData: TokenFormData) => {
    try {
      const tokenPayload: Token = {
        id: editingToken?.id,
        tokenName: tokenData.tokenName,
        symbol: tokenData.symbol,
        tokenAddress: tokenData.tokenAddress,
        openingBal: typeof tokenData.openingBalance === 'number' ? tokenData.openingBalance.toString() : tokenData.openingBalance as any || '0',
        openingDate: tokenData.openingDate || new Date().toISOString().split('T')[0],
        currentBal: typeof tokenData.currentBal === 'number' ? tokenData.currentBal.toString() : tokenData.currentBal as any || '0',
        notes: tokenData.notes || '',
        isActive: tokenData.status === 'active',
      };

      const response = await saveTokenAPI(tokenPayload);
      
      if (response.isSuccess) {
        showSuccess(editingToken ? 'Token updated successfully!' : 'Token created successfully!');
        await loadData();
        setIsModalOpen(false);
        setEditingToken(null);
      } else {
        showError(response.message || 'Failed to save token');
      }
    } catch (error) {
      console.error('Failed to save token:', error);
      showError('Failed to save token. Please try again.');
    }
  };

  const handleEdit = (token: Token) => {
    setEditingToken(token);
    setIsModalOpen(true);
  };

  const handleDelete = async (token: Token) => {
    if (window.confirm(`Are you sure you want to delete ${token.tokenName}?`)) {
      try {
        const response = await deleteTokenAPI(token.id || 0);
        if (response.isSuccess) {
          await loadData();
          showSuccess('Token deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete token');
        }
      } catch (error) {
        console.error('Failed to delete token:', error);
        showError('Failed to delete token. Please try again.');
      }
    }
  };

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>Token Master</h1>
          <p className={styles.subtitle}>Manage your tokens and wallet settings</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddToken}>
            + Add Token
          </button>
        )}
      </div>

      {/* Table */}
      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading tokens...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>TOKEN NAME</th>
                <th>SYMBOL</th>
                <th>ADDRESS</th>
                <th>OPENING BAL</th>
                <th>CURRENT BAL</th>
                <th>STATUS</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {tokens.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 7 : 6} className={styles.emptyState}>No tokens found</td>
                </tr>
              ) : (
                tokens.map((token) => (
                  <tr key={token.id}>
                    <td>{token.tokenName}</td>
                    <td className={styles.symbol}>{token.symbol}</td>
                    <td>{token.tokenAddress}</td>
                    <td>{parseFloat(token.openingBal).toFixed(4)}</td>
                    <td>{parseFloat(token.currentBal).toFixed(4)}</td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[token.isActive ? 'active' : 'inactive']}`}>
                        {token.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(token)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(token)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Token Modal */}
      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingToken ? 'Edit Token' : 'Add New Token'}
      >
        <AddTokenForm 
          onSubmit={handleSubmitToken} 
          onCancel={handleCloseModal}
          initialData={editingToken ? {
            tokenName: editingToken.tokenName,
            symbol: editingToken.symbol,
            tokenAddress: editingToken.tokenAddress,
            openingBalance: parseFloat(editingToken.openingBal),
            openingDate: editingToken.openingDate ? editingToken.openingDate.split('T')[0] : new Date().toISOString().split('T')[0],
            status: editingToken.isActive ? 'active' : 'inactive',
            notes: editingToken.notes,
          } : undefined}
        />
      </Modal>
    </div>
  );
};

export default TokenMaster;
